import { BreakpointObserver } from '@angular/cdk/layout';
import { Location, ViewportScroller } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    Cms,
    ICarDetails,
    ILeasingUsedCarPriceResponse,
    TempAny,
} from '@impact/data';
import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { calculatorIcon, checkmarkIcon, chevronRight } from '../../icons';
import { CheckoutService } from '../checkout/checkout.service';
import { HeaderService } from '../core/header.service';
import { objectIsEmpty, sanitizeVehicleUrl } from '../core/helpers';
import { SettingsService } from '../core/settings.service';
import { FILTER_FACET } from '../filter-service/filter.models';
import { ILeasingConfiguratorRanges } from '../leasing/configurator/leasing-configurator.component';
import {
    ILeasingConfiguratorData,
    LEASING_AGREEMENT_TYPES,
    LeasingService,
} from '../leasing/leasing.service';
import { NavigationService } from '../navigation/navigation.service';
import { SidePanelService } from '../side-panel/side-panel.service';
import { GridContext } from '../umbraco-grid/umb-grid/umb-grid.component';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';
import { UrlifyPipe } from '../utils/urlify.pipe';
import {
    IUsedCarPaymentOptions,
    IUsedCarSpecifications,
    UsedCarPageSections,
    UsedCarSpecTypes,
} from './used-car.models';
import { UsedCarService } from './used-car.service';

enum LayoutView {
    SMALL = 'Small',
    LARGE = 'Large',
}

export interface IExpandedInsuranceCompany extends Cms.InsuranceCompany {
    isSelected?: boolean;
}

@Component({
    selector: 'app-used-car-details',
    template: `
        <!-- LARGE LAYOUT PAGE HEADER -->
        <ng-container *ngIf="componentLayout == layoutViewEnum.LARGE">
            <!-- Page Navigation -->
            <ng-container *ngTemplateOutlet="pageNavigation"></ng-container>

            <!-- Breadcrumbs -->
            <nav role="navigation" class="used-car-details__breadcrumbs-wrapper">
                <ol class="used-car-details__breadcrumbs-list" *ngIf="breadcrumbData?.breadcrumbPath">
                    <li class="used-car-details__breadcrumbs-list-item" *ngIf="breadcrumbData?.breadcrumbPath.length > 1">
                        <a class="used-car-details__breadcrumbs-link" [routerLink]="breadcrumbData.currentPage?.url">
                            {{ 'used_car_details.breadcrumb_all_prefix_text' | translate }} {{ carDetails.make?.value }}
                            <ng-container *ngIf="breadcrumbData?.hasModel">{{ carDetails.model?.value }}</ng-container>
                        </a>
                    </li>
                    <li class="used-car-details__breadcrumbs-list-item" *ngFor="let item of breadcrumbData.breadcrumbPath; let i = index">
                        <a class="used-car-details__breadcrumbs-link"
                            [routerLink]="item.url">
                            {{ item.name }}
                        </a>
                    </li>
                    <ng-container *ngIf="breadcrumbData?.breadcrumbPath.length === 1">
                        <li class="used-car-details__breadcrumbs-list-item">
                            <a class="used-car-details__breadcrumbs-link"
                                [routerLink]="breadcrumbData?.breadcrumbPath[0]?.url"
                                [queryParams]="{ 'make.keyword': carDetails.make?.value?.toLowerCase() }">
                                {{ carDetails.make?.value }}
                            </a>
                        </li>
                    </ng-container>
                </ol>
            </nav>
        </ng-container>

        <!-- PAGE CONTENT -->
        <div class="used-car-details__layout" [ngSwitch]="componentLayout">
            <!-- SMALL LAYOUT -->
            <ng-container *ngSwitchCase="layoutViewEnum.SMALL">
                <div #overviewRef [id]="UsedCarPageSections.OVERVIEW" class="used-car-hero__wrapper">
                    <div class="layout-wrapper">
                        <ng-container *ngTemplateOutlet="vehicleDetailsHeader"></ng-container>
                    </div>

                    <!-- Gallery -->
                    <app-used-car-hero [car]="carDetails"></app-used-car-hero>

                    <!-- Navigation -->
                    <ng-container *ngTemplateOutlet="pageNavigation"></ng-container>

                    <!-- Financing -->
                    <div class="used-car-details__content-section">
                        <div class="used-cars-details__section-header">
                            <div class="used-cars-details__section-header-text">{{ 'used_car_details.menu_financing' | translate }}</div>
                        </div>
                        <ng-container *ngTemplateOutlet="vehicleDetailsFinancing"></ng-container>
                    </div>
                </div>

                <!-- CONTENT -->
                <ng-container *ngTemplateOutlet="usedCarPageContent"></ng-container>
            </ng-container>

            <!-- LARGE LAYOUT -->
            <ng-container *ngSwitchCase="layoutViewEnum.LARGE">
                <!-- Sidebar Content -->
                <div class="used-car-details__layout-sidebar" *ngIf="carDetails" #scrollingSidebarParentRef>
                    <div class="used-car-details__layout-sidebar-content" #scrollingSidebarRef>
                        <div #sidebarContentRef>
                            <ng-container *ngTemplateOutlet="vehicleDetailsHeader"></ng-container>
                            <ng-container *ngTemplateOutlet="vehicleDetailsFinancing"></ng-container>
                        </div>
                    </div>
                </div>

                <!-- Page Content -->
                <div class="used-car-details__layout-content">
                    <!-- Gallery -->
                    <div class="used-car-details__gallery">
                        <div #overviewRef [id]="UsedCarPageSections.OVERVIEW" class="used-car-hero__wrapper">
                            <app-used-car-hero [car]="carDetails"></app-used-car-hero>
                        </div>
                    </div>

                    <!-- CONTENT -->
                    <ng-container *ngTemplateOutlet="usedCarPageContent"></ng-container>
                </div>
            </ng-container>
        </div>

        <!--
        TEMPLATES
        -->

        <!-- Page Content -->
        <ng-template #usedCarPageContent>
            <!-- Vehicle specifications -->
            <div class="used-car-details__content-section">
                <app-used-car-main-details></app-used-car-main-details>

                <app-product-item-valuation-badge
                    class="used-car-valuation-badge"
                    [valuationString]="carDetails.indicataPriceLevel?.value"
                    [isClickable]="true"
                    [minimalLayout]="false">
                </app-product-item-valuation-badge>
            </div>

            <!-- EQUIPMENT -->
            <div class="used-car-details__content-section" #equipmentRef [id]="UsedCarPageSections.EQUIPMENT">
                <div class="used-cars-details__section-header">
                    <div class="used-cars-details__section-header-text">{{ 'used_car_details.page_section_equipment_header' | translate }}</div>
                </div>
                <app-used-car-selected-equipment [equipment]="carDetails.equipmentList"></app-used-car-selected-equipment>
            </div>

            <!-- SPECIFICATIONS -->
            <div class="used-car-details__content-section">
                <div class="used-cars-details__section-header">
                    <div class="used-cars-details__section-header-text">{{ 'used_car_details.page_section_details_header' | translate }}</div>
                </div>
                <app-used-car-equipment-comment [data]="carDetails.comment?.value"></app-used-car-equipment-comment>
            </div>

            <!-- SPECIFICATIONS -->
            <div class="used-car-details__content-section" #specificationsRef [id]="UsedCarPageSections.SPECIFICATIONS" *ngIf="specifications">
                <div class="used-cars-details__section-header centered">
                    <div class="used-cars-details__section-header-category-text">{{ 'used_car_details.page_section_specifications_header_secondary' | translate }}</div>
                    <div class="used-cars-details__section-header-text">{{ 'used_car_details.page_section_specifications_header' | translate }}</div>
                </div>
                <app-used-car-specifications [specifications]="specifications"></app-used-car-specifications>
            </div>

            <!-- SERVICES -->
            <div class="used-car-details__content-section" #serviceRef [id]="UsedCarPageSections.SERVICE" *ngIf="showServices">
                <div class="used-cars-details__section-header">
                    <div class="used-cars-details__section-header-category-text">[Serviceabonnement]</div>
                    <div class="used-cars-details__section-header-text">[Spar optil 15% pÃ¥ vÃ¦rkstedsregningen*]</div>
                    <div class="used-cars-details__section-header-intro-text">[Med et serviceabonnement slipper du for overraskelser i budgettet for et fast mÃ¥nedligt belÃ¸b.]</div>
                </div>

                <div class="service-subscription-spot">
                    <h5>[Service abonnement indhold...]</h5>
                </div>
            </div>

            <!-- EXCHANGE CALCULATION -->
            <div class="used-car-details__content-section" #exchangeCalculationRef [id]="UsedCarPageSections.EXCHANGE_CALCULATION">
                <div class="exchange-vehicle-spot">
                    <div class="used-cars-details__section-header">
                        <div class="used-cars-details__section-header-category-text">{{ 'used_car_details.page_section_exchange_header_secondary' | translate }}</div>
                        <div class="used-cars-details__section-header-text">{{ 'used_car_details.page_section_exchange_header' | translate }}</div>
                    </div>
                    <div class="exchange-vehicle-spot__content">
                        <div class="exchange-vehicle-spot__content-block">
                            {{ 'used_car_details.page_section_exchange_text' | translate }}
                        </div>
                        <div class="exchange-vehicle-spot__content-block">
                            <app-input-licenseplate [label]="'indicata.registration_number____input_name' | translate"></app-input-licenseplate>
                        </div>
                    </div>
                </div>
            </div>

            <!-- USP -->
            <div class="used-car-details__content-section">
                <app-usp class="used-car-details-usp" [items]="pageData?.usp"></app-usp>
            </div>

            <!-- Content spots grid 1 -->
            <div class="used-car-details__content-section" *ngIf="isElectric && pageData?.contentSpotsGrid1">
                <app-spot-grid [grid]="pageData?.contentSpotsGrid1" [context]="GridContext.PDP"></app-spot-grid>
            </div>

            <!-- INSURANCE -->
            <div class="used-car-details__content-section" #insuranceRef [id]="UsedCarPageSections.INSURANCE" *ngIf="insuranceCompanies.length">
                <div class="used-car-details-insurance-spot">
                    <div class="used-cars-details__section-header">
                        <div class="used-cars-details__section-header-category-text">{{ 'used_car_details.page_section_insurance_header_secondary' | translate }}</div>
                        <div class="used-cars-details__section-header-text">{{ 'used_car_details.page_section_insurance_header' | translate }}</div>
                        <div class="used-cars-details__section-header-intro-text" *ngIf="pageData?.insuranceSectionIntroText">{{ pageData?.insuranceSectionIntroText }}</div>
                    </div>

                    <div class="used-car-details-insurance-spot__content">
                        <ul class="used-car-details-insurance-spot__list">
                            <li *ngFor="let company of insuranceCompanies">
                                <button type="button"
                                    (click)="selectInsurance(company)"
                                    class="used-car-details-insurance-spot__item"
                                    [ngClass]="{'active-option': !!company?.isSelected}"
                                >
                                    <img *ngIf="company.companyLogo?.src" [src]="company.companyLogo?.src | resolveMediaUrl: { format: 'webp', height: 44 }" [alt]="company?.companyName" class="used-car-details-insurance-spot__item-image" />
                                    <app-rich-text *ngIf="company?.promotionalContent" [html]="company?.promotionalContent" class="used-car-details-insurance-spot__item-text"></app-rich-text>
                                    <div class="used-car-details-insurance-spot__item-price">{{ company?.priceText }}</div>
                                    <div class="used-car-details-insurance-spot__item-terms">{{ company?.companyTerms }}</div>
                                    <span class="used-car-details-insurance-spot__item-active">${checkmarkIcon}</span>
                                </button>
                            </li>
                        </ul>
                        <a *ngIf="pageData?.insuranceGuidePageLink" appInterceptLinks
                            [routerLink]="pageData?.insuranceGuidePageLink?.cleanUrl"
                            class="used-car-details-insurance-spot__content-link">
                            {{ pageData?.insuranceGuidePageLink?.name }}
                        </a>
                    </div>

                    <div class="used-car-details-insurance-spot__disclaimer" *ngIf="pageData?.insuranceSectionDisclaimerText">{{ pageData?.insuranceSectionDisclaimerText }}</div>
                </div>
            </div>

            <!-- Content spots grid 2 -->
            <div [id]="UsedCarPageSections.LAST_SECTION" #lastSectionRef>
                <div class="used-car-details__content-section" *ngIf="pageData?.contentSpotsGrid2">
                    <app-spot-grid [grid]="pageData?.contentSpotsGrid2" [context]="GridContext.PDP"></app-spot-grid>
                </div>
            </div>
        </ng-template>

        <!-- Page Navigation Template -->
        <ng-template #pageNavigation>
            <div class="scroll-sticky-wrapper used-car-menu"
                #headerStickyScrollRef
                [ngStyle]="{ height: stickyHeaderElementHeight }"
                [ngClass]="{ 'scroll-sticky-wrapper--is-sticky': stickyHeaderIsFixed }"
            >
                <div class="page-navigation-header used-car-details__page-navigation-header" [style.top]="moveMenuDown ? headerHeight + 'px' : ''">
                    <div class="page-navigation-header__wrapper">
                        <div class="page-navigation-header__column-center">
                            <nav class="used-car-menu-nav">
                                <ul class="used-car-menu-nav__list" #listElements>
                                    <li class="used-car-menu-nav__list-item" #listElement>
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.OVERVIEW }"
                                            (click)="scrollToSection(UsedCarPageSections.OVERVIEW)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                 {{ 'used_car_details.navigation_overview_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                    <li class="used-car-menu-nav__list-item" #listElement>
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.EQUIPMENT }"
                                            (click)="scrollToSection(UsedCarPageSections.EQUIPMENT)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                {{ 'used_car_details.navigation_equipment_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                    <li class="used-car-menu-nav__list-item" #listElement *ngIf="specifications">
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.SPECIFICATIONS }"
                                            (click)="scrollToSection(UsedCarPageSections.SPECIFICATIONS)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                {{ 'used_car_details.navigation_specifications_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                    <li class="used-car-menu-nav__list-item" #listElement *ngIf="showServices">
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.SERVICE }"
                                            (click)="scrollToSection(UsedCarPageSections.SERVICE)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                {{ 'used_car_details.navigation_service_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                    <li class="used-car-menu-nav__list-item" #listElement>
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.EXCHANGE_CALCULATION }"
                                            (click)="scrollToSection(UsedCarPageSections.EXCHANGE_CALCULATION)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                {{ 'used_car_details.navigation_exchange_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                    <li class="used-car-menu-nav__list-item" #listElement *ngIf="insuranceCompanies.length">
                                        <button
                                            class="page-secondary-navigation__item-link used-car-menu-nav__link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === UsedCarPageSections.INSURANCE }"
                                            (click)="scrollToSection(UsedCarPageSections.INSURANCE)"
                                        >
                                            <span class="page-secondary-navigation__item-text">
                                                {{ 'used_car_details.navigation_insurance_section' | translate }}
                                            </span>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    </div>
                </div>
            </div>
        </ng-template>

        <!-- Vehicle Details header -->
        <ng-template #vehicleDetailsHeader>
            <div class="used-car-details__info" *ngIf="carDetails">
                <h1 class="used-car-details__info-header">
                    {{ carDetails.make?.value }} {{ carDetails.model?.value }}
                    {{ carDetails.variant?.value }}
                </h1>
                <div class="used-car-details__info-bottom">
                    <div class="used-car-details__info-details">
                        <div [attr.aria-label]="'Årgang ' + carDetails.year?.value">Årgang {{carDetails.year?.value}}</div>
                        <span class="used-car-details__info-details-divider"></span>
                        <div [attr.aria-label]="'Kørt ' + carDetails.mileage?.data + ' kilometer'">{{carDetails.mileage?.data | number}} km.</div>
                    </div>
                    <button type="button"
                        class="button--as-link used-car-details__info-button"
                        (click)="showExhangeCarSidepanel()">
                        ${calculatorIcon} {{ 'used_car_details.exchange_car_link_text' | translate }} ${chevronRight}</button>
                </div>
            </div>
        </ng-template>

        <!-- Vehicle details financing -->
        <ng-template #vehicleDetailsFinancing>
            <app-used-car-financing-sidebar
                [loanAmount]="carDetails.price?.data"
                [registrationDate]="carDetails.registrationDate?.data"
                [isBuyable]="carDetails.isBuyable?.data"
                [allowLeasing]="allowLeasing"
                [leasingDataTexts]="leasingDataTexts"
                [showServices]="showServices"
                (scrollToSection)="scrollToSection($event)"
                [insuranceCompany]="selectedInsuranceCompany"
                (insuranceToggleClick)="selectInsurance($event)">
            </app-used-car-financing-sidebar>
        </ng-template>

        <div class="book-test-drive-cta-wrapper" *ngIf="bookTestDriveCtaData?.bookTestDriveCtaCategoryHeaderText">
            <app-book-used-car-test-drive-cta [data]="bookTestDriveCtaData"></app-book-used-car-test-drive-cta>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsedCarDetailsComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked {
    @ViewChild('headerStickyScrollRef') headerStickyScrollRef: ElementRef;
    @ViewChild('scrollingSidebarParentRef') scrollingSidebarParentRef: ElementRef;
    @ViewChild('scrollingSidebarRef') scrollingSidebarRef: ElementRef;
    @ViewChildren('listElement') listElements: QueryList<ElementRef>;
    @ViewChild('overviewRef') overviewRef: ElementRef;
    @ViewChild('equipmentRef') equipmentRef: ElementRef;
    @ViewChild('specificationsRef') specificationsRef: ElementRef;
    @ViewChild('serviceRef') serviceRef: ElementRef;
    @ViewChild('exchangeCalculationRef') exchangeCalculationRef: ElementRef;
    @ViewChild('insuranceRef') insuranceRef: ElementRef;
    @ViewChild('lastSectionRef') lastSectionRef: ElementRef;
    @ViewChild('sidebarContentRef') sidebarContentRef: ElementRef;
    @Input() carDetails: ICarDetails;
    @Input() pageData: Cms.UsedCarDetailsPage;

    // NEW
    layoutViewEnum = LayoutView;
    GridContext = GridContext;
    componentLayout: string;
    private mutationObserver: MutationObserver;
    showServices = false; // Temporary to hide all things related to services

    paymentOptions: IUsedCarPaymentOptions;
    specifications: IUsedCarSpecifications;
    usedCarDetailsLinkData: TempAny;
    usedCarsOverviewPage: TempAny;
    b2bUsedCarsOverviewPageUrl: string;

    usedCarsVehicleMakesPage: TempAny;
    usedCarsNavigationData: Cms.NavigationDto[];
    breadcrumbData: TempAny;

    isIOS: boolean;
    isElectric: boolean;

    UsedCarPageSections = UsedCarPageSections;
    deactivateScrollTrigger: boolean;
    listElementsArray: TempAny[];
    sectionsScrollOffset: number;

    overviewElementYPosition: number;
    overviewElementHeight: number;
    equipmentsElementYPosition: number;
    equipmentsElementHeight: number;
    specificationsElementYPosition: number;
    specificationsElementHeight: number;
    serviceElementYPosition: number;
    serviceElementHeight: number;
    exchangeCalculationElementYPosition: number;
    exchangeCalculationElementHeight: number;
    insuranceElementYPosition: number;
    insuranceElementHeight: number;
    lastSectionElementYPosition: number;

    insuranceCompanies: IExpandedInsuranceCompany[];
    selectedInsuranceCompany: IExpandedInsuranceCompany | null;

    activeScrollMenuItem: UsedCarPageSections | undefined;

    bookTestDriveCtaData: Cms.BookUsedCarTestDriveCtaSpot;

    // Leasing
    isLeasable: boolean;
    allowLeasing: boolean;
    leasingDataTexts: TempAny;
    carData: ICarDetails;
    leasingConfiguratorData: ILeasingConfiguratorData;
    leasingConfiguratorRangesData: ILeasingConfiguratorRanges;
    carIntervalsReqBody: TempAny;

    ///
    /// Sticky Header properties
    ///
    stickyHeaderElement: HTMLElement;
    stickyHeaderElementHeight: string;
    stickyHeaderElementYPosition: number;
    stickyHeaderIsFixed: boolean;
    moveMenuDown: boolean;
    headerHeight: number;
    prevScrollPosition: number;

    ///
    /// Sticky SideBar properties
    ///
    staticSidebar: boolean;
    scrollingSidebarParentElement: HTMLElement;
    scrollingSidebarElement: HTMLElement;
    scrollingSidebarOffset = 30;
    scrollingSidebarOffsetWithStickyHeader = 130;

    private subscription: Subscription = new Subscription();

    private unsubscribe = new Subject<void>();

    /**
     * Handles window scroll event to update active scroll menu item based on current scroll position.
     *
     * Checks if the scroll position is within the bounds of specific sections (Overview, Equipments, Specifications, Service, Exchange calculation, Insurance) and updates the active scroll menu item accordingly.
     *
     * If the scroll position is beyond the last section, sets the active scroll menu item to undefined.
     *
     * @return {void}
     */
    @HostListener('window:scroll')
    onWindowScroll(): void {
        if (!this.featureDetectionService.isBrowser() || this.deactivateScrollTrigger) {
            return;
        }

        interface Section {
            name: UsedCarPageSections;
            yPosition: number;
            height: number;
            index: number | undefined;
        }

        const sections: Section[] = [
            { name: UsedCarPageSections.LAST_SECTION, yPosition: this.lastSectionElementYPosition, height: 0, index: undefined },
            { name: UsedCarPageSections.INSURANCE, yPosition: this.insuranceElementYPosition, height: this.insuranceElementHeight, index: 4 },
            { name: UsedCarPageSections.EXCHANGE_CALCULATION, yPosition: this.exchangeCalculationElementYPosition, height: this.exchangeCalculationElementHeight, index: 3 },
            // TODO: Add services when needed
            // { name: UsedCarPageSections.SERVICE, yPosition: this.serviceElementYPosition, height: this.serviceElementHeight, index: 3 },
            { name: UsedCarPageSections.SPECIFICATIONS, yPosition: this.specificationsElementYPosition, height: this.specificationsElementHeight, index: 2 },
            { name: UsedCarPageSections.EQUIPMENT, yPosition: this.equipmentsElementYPosition, height: this.equipmentsElementHeight, index: 1 },
            { name: UsedCarPageSections.OVERVIEW, yPosition: this.overviewElementYPosition, height: this.overviewElementHeight, index: 0 },
        ];

        for (const section of sections) {
            if (this.elmentScrollTriggerPosition(window.scrollY, section.yPosition, section.height)) {
                this.activeScrollMenuItem = section.name;

                if (section.index !== undefined) {
                    this.scrollToListItem(section.index);
                } else {
                    this.activeScrollMenuItem = undefined;
                }

                this.calculateSections();

                break;
            }
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private featureDetectionService: FeatureDetectionService,
        private readonly headerService: HeaderService,
        private route: ActivatedRoute,
        private scroller: ViewportScroller,
        private settingsService: SettingsService,
        private readonly navigationService: NavigationService,
        private usedCarService: UsedCarService,
        public location: Location,
        private leasingService: LeasingService,
        private breakpointObserver: BreakpointObserver,
        private sidepanelService: SidePanelService,
        private checkoutService: CheckoutService,
        private readonly router: Router,
    ) {
        this.activeScrollMenuItem = UsedCarPageSections.OVERVIEW;

        this.featureDetectionService.onResize$.pipe(takeUntil(this.unsubscribe)).subscribe((viewport) => {
            if (viewport && this.headerStickyScrollRef) {
                this.stickyHeaderElementYPosition = this.headerStickyScrollRef.nativeElement.getBoundingClientRect().top + window.scrollY;
            }

            this.checkSidebarContentSize();

            if (this.scrollingSidebarParentRef) {
                this.scrollingSidebarParentElement = this.scrollingSidebarParentRef.nativeElement;
                this.scrollingSidebarElement = this.scrollingSidebarRef.nativeElement;
                this.updateScrollingSidebarWidth();
                this.updateScrollingSidebarPosition();
            }
        });

        this.subscription.add(this.settingsService.get().subscribe((settings) => {
            this.insuranceCompanies = settings.insuranceCompanies.filter(
                (company: IExpandedInsuranceCompany) => company.promotedCompany === true
            ).map(
                (company: IExpandedInsuranceCompany, index: number) => ({
                    ...company,
                    isSelected: index === 0 // Set isSelected to true for the first company, false for others
                })
            ) || [];

            if (this.insuranceCompanies.length) {
                this.selectedInsuranceCompany = {
                    ...this.insuranceCompanies[0],
                    isSelected: true
                };
            } else {
                this.selectedInsuranceCompany = null;
            }
        }));
    }

    ngOnInit(): void {
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
        this.isElectric = this.carDetails.propellant?.data === 'El';

        // Detect viewport resize
        this.detectLayout();

        // Fetch Car data, then leasing data
        if (this.carDetails) {
            const carData = this.carDetails;

            this.subscription.add(
                combineLatest([
                    this.settingsService.getGlobalPages(),
                    this.navigationService.usedCarsNavigation$
                ]).pipe(takeUntil(this.unsubscribe))
                .subscribe(([globalPages, usedCarsNavigation]) => {
                    this.usedCarsOverviewPage = globalPages.usedCarsOverviewPage;
                    this.usedCarsVehicleMakesPage = globalPages.usedCarsVehicleMakesPage;
                    this.usedCarsNavigationData = usedCarsNavigation;
                    this.b2bUsedCarsOverviewPageUrl = globalPages.b2bUsedCarsOverviewPage?.url ?? undefined;

                    if (globalPages.usedCarsVehicleMakesPage) {
                        const sanitizedUrl = sanitizeVehicleUrl(`${this.usedCarsVehicleMakesPage.url}/${this.carDetails.make?.value}/${this.carDetails.model?.value}`);
                        this.generateBreadCrumb(sanitizedUrl, this.carDetails.model?.value ? true : false);
                    } else {
                        console.warn('[BN: Missing usedCarsVehicleMakesPage from umbraco settings]');
                    }
                }));

            const hasSteelPrice = carData.steelPrice?.data !== 0;
            const regFees = carData.registrationFee?.data !== 0;
            const isElectric = carData.propellant?.data === 'El'

            this.allowLeasing = (regFees && hasSteelPrice) || (isElectric && hasSteelPrice);

            // Set default values
            this.leasingConfiguratorData = {
                downPayment: 0,
                monthlyPayment: 0,
                monthlyTerms: 0,
                yearlyDistanceInKm: 0,
                residualValue: 0,
                selectedLeasingPlanType: LEASING_AGREEMENT_TYPES.OPERATIONAL
            };

            // Leasing configurator ranges data
            this.leasingConfiguratorRangesData = {
                downPaymentRange: {
                    minValue: 0,
                    maxValue: 0
                },
                leasingMonthsRange: [],
                yearlyDistanceInKmRange: {
                    minValue: 0,
                    maxValue: 0
                },
                residualValueRange: {
                    minValue: 0,
                    maxValue: 0
                },
            };

            this.carIntervalsReqBody = {
                carMilage: carData?.mileage?.data,
                firstRegistrationDate: String(carData.registrationDate?.data),
                fueltype: carData?.propellant?.data === 'Benzin' ? 'B' : carData?.propellant?.data === 'El' ? 'E' : 'D',
                isLeasingTypeFinancial: false,
                isLeasingForBusiness: false,
                make: carData?.make?.value,
                model: carData?.model?.value,
                modelDescription: carData?.variant?.value,
                password: '',
                steelprice: carData?.steelPrice?.data,
                vehicleType: 'P',
            };

            this.getLeasingData(carData);
        }

        // Format details text
        if (this.carDetails.comment?.value) {
            const commentRegex = new RegExp(/(?:\\[rn]|[\r\n]+)+/g);
            this.carDetails.comment.value = this.carDetails.comment?.value?.replace(commentRegex, '<br>');
        }

        this.usedCarDetailsLinkData = {
            usedCarsOverviewPageUrl: `/${this.route?.snapshot?.url[0]?.path}`,
            makeParamKey: FILTER_FACET.MAKE,
            makeParamValue: new UrlifyPipe().transform(this.carDetails.make?.value ?? ''),
            makeParamText: this.carDetails.make?.value,
            modelParamKey: FILTER_FACET.MODEL,
            modelParamValue: new UrlifyPipe().transform(this.carDetails.model?.value ?? ''),
            modelParamText: this.carDetails.model?.value,
        };

        this.bookTestDriveCtaData = {
            bookTestDriveCtaCategoryHeaderText: this.pageData?.bookTestDriveCtaCategoryHeaderText,
            bookTestDriveCtaHeaderText: this.pageData?.bookTestDriveCtaHeaderText,
            bookTestDriveCtaText: this.pageData?.bookTestDriveCtaText,
            id: this.pageData?.id,
            name: this.pageData?.name,
            type: this.pageData?.type,
        };

        this.usedCarService.setCarDetails(this.carDetails);
        this.usedCarService.setPageData(this.pageData);
        this.paymentOptions = {
            cash: { ...this.carDetails.cashPrice, vatIncluded: this.carDetails.priceIncludesVat?.data },
            loanPerMonth: this.carDetails.santanderPaymentPerMonth,
            loanDownPayment: this.carDetails.santanderDownPayment,
        };

        if (this.featureDetectionService.isBrowser()) {
            this.sectionsScrollOffset = window.innerHeight / 2;
            this.scrollToSection(this.UsedCarPageSections.OVERVIEW);
        }

        this.specifications = {
            main: {
                acceleration: this.carDetails.acceleration0To100,
                kmPerLiter: this.carDetails.kmPerLiter,
                topSpeed: this.carDetails.topSpeed,
            },
            allSpecifications: this.carDetails.specificationGroups
                ? Object.entries(this.carDetails.specificationGroups).map(([key, value]) => ({
                      ...value,
                      type: key as UsedCarSpecTypes,
                  }))
                : undefined,
            disclaimerText: this.pageData?.disclaimerSpecificationsText,
        };

        // Sticky header:
        this.headerService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.handleModelHeaderPosition(position);
                this.updateScrollingSidebarPosition();
            });

        this.headerService
            .getHeaderEvents()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((height: number) => {
                this.headerHeight = height;
                this.prevScrollPosition = height;
                this.cdr.markForCheck();
            });
    }

    ngAfterViewInit(): void {
        if (this.componentLayout === LayoutView.LARGE) {
            // Initial check on component load
            this.checkSidebarContentSize();

            // Create a MutationObserver to detect changes inside the sidebarContentRef element
            this.mutationObserver = new MutationObserver(() => {
                this.checkSidebarContentSize();
            });

            // Start observing changes in child nodes and subtree (inside the sidebarContentRef)
            this.mutationObserver.observe(this.sidebarContentRef.nativeElement, {
                childList: true,
                subtree: true,
                characterData: true,
            });
        }

        if (this.listElements) {
            this.listElementsArray = this.listElements.toArray();
        }

        if (this.headerStickyScrollRef) {
            this.stickyHeaderElement = this.headerStickyScrollRef.nativeElement;

            if (this.headerStickyScrollRef.nativeElement.getBoundingClientRect) {
                this.stickyHeaderElementYPosition = this.headerStickyScrollRef.nativeElement.getBoundingClientRect()?.top + window.scrollY;
            }
        }

        if (this.scrollingSidebarParentRef) {
            this.scrollingSidebarParentElement = this.scrollingSidebarParentRef.nativeElement;
            this.scrollingSidebarElement = this.scrollingSidebarRef.nativeElement;

            if (this.scrollingSidebarParentRef.nativeElement.getBoundingClientRect) {
                this.updateScrollingSidebarWidth();
            }
        }
    }

    ngAfterContentChecked(): void {
        if (this.featureDetectionService.isBrowser()) {
            this.calculateSections();
        }
    }

    /**
     * Checks the height of the sidebar content and sets it to static or sticky depending on the height.
     *
     * @return {void}
     */
    checkSidebarContentSize() {
        if (this.componentLayout === LayoutView.SMALL) {
            return;
        }

        const elementHeight = this.sidebarContentRef.nativeElement.offsetHeight;
        const viewportHeight = window.innerHeight;
        let headerOffset = 0;
        const verticalSpacing = 40;

        if (this.moveMenuDown || window.scrollY < this.headerHeight) {
            headerOffset = this.headerHeight + 100;
        } else {
            headerOffset = 100;
        }

        if (elementHeight > viewportHeight - headerOffset - verticalSpacing) {
            this.staticSidebar = true;
        } else {
            this.staticSidebar = false;
        }
    }

    /**
     * Detects layout changes and updates the layout view accordingly.
     *
     * This function observes the viewport width using the breakpointObserver
     * and updates the layout view based on whether the viewport width is
     * greater than or equal to 1200px.
     *
     * @return {void} This function does not return anything.
     */
    detectLayout(): void {
        // Detect layout changes
        const queries$ = this.breakpointObserver.observe([
            '(min-width: 1200px)',
        ]);

        queries$.pipe(takeUntil(this.unsubscribe)).subscribe((result: any) => {
            if (!result.matches) {
                this.updateLayout(this.layoutViewEnum.SMALL);
            } else {
                this.updateLayout(this.layoutViewEnum.LARGE);
            }
        });
    }


    /**
     * Updates the layout view based on the provided layout parameter.
     *
     * @param {LayoutView} layout - The layout to update to.
     *
     * This function updates the componentLayout property and sets the
     * scrollbar offset based on the provided layout. If the layout is
     * changed, it also updates the stickyHeaderElementYPosition and
     * calls the markForCheck method on the change detector to trigger
     * a change detection cycle.
     */
    updateLayout(layout: LayoutView): void {
        if (this.componentLayout !== layout) {
            this.componentLayout = layout;

            this.scroller.setOffset([0, layout === LayoutView.SMALL ? 70 : 200]);

            if (this.headerStickyScrollRef) {
                this.stickyHeaderElementYPosition = this.headerStickyScrollRef.nativeElement.getBoundingClientRect()?.top + window.scrollY;
            }

            this.cdr.markForCheck();
        }
    }

    /**
     * Generates breadcrumb data based on the provided URL and whether the model is included.
     *
     * @param {string} url - The URL to generate breadcrumb data for.
     * @param {boolean} hasModel - Whether the model is included in the breadcrumb data. Defaults to true.
     * @return {void} This function does not return anything.
     */
    generateBreadCrumb(url: string, hasModel = true, attempts = 0, processedUrls = new Set<string>()) {
        const maxAttempts = 5; // Set a reasonable max attempt count to avoid infinite recursion

        // Prevent redundant processing of the same URL
        if (processedUrls.has(url) || attempts >= maxAttempts) {
            this.breadcrumbData = this.getDefaultBreadcrumb(); // Fallback breadcrumb if all attempts fail
            this.cdr.markForCheck();
            return;
        }

        // Add URL to processed set and increment attempt count
        processedUrls.add(url);
        const searchResult = this.breadcrumb(this.usedCarsNavigationData, url);

        if (searchResult) {
            searchResult.breadcrumbPath.unshift(this.usedCarsOverviewPage);
            searchResult.hasModel = hasModel;
            this.breadcrumbData = searchResult;
            this.cdr.markForCheck();
        } else {
            // Backup breadcrumb with only the make if model page isn't found in navigation data
            const sanitizedUrl = sanitizeVehicleUrl(`${this.usedCarsVehicleMakesPage.url}/${this.carDetails.make?.value}`);

            // Call generateBreadCrumb recursively with incremented attempts
            this.generateBreadCrumb(sanitizedUrl, false, attempts + 1, processedUrls);
        }
    }

    // Default breadcrumb method as a fallback if no match found
    getDefaultBreadcrumb(): TempAny {
        return {
            breadcrumbPath: [this.usedCarsOverviewPage],
            hasModel: false,
        };
    }

    /**
     * Searches for the deepest matching page based on the provided URL in the navigation data.
     *
     * @param {Cms.NavigationDto[]} pages - The navigation data to search in.
     * @param {string} targetUrl - The URL to search for.
     *
     * @return {TempAny | undefined} The deepest matching page or undefined if no match is found.
     */
    breadcrumb(pages: Cms.NavigationDto[], targetUrl: string): TempAny | undefined {
        function findPageRecursive(currentPage: Cms.NavigationDto, parentInfo: TempAny[] = []): TempAny | undefined {
            const currentUrl = currentPage.url;
            const currentName = currentPage.name || '';

            const updatedParents = [...parentInfo, { url: currentUrl, name: currentName }];

            if (currentUrl === targetUrl) {
                return { currentPage, breadcrumbPath: updatedParents };
            }

            if (currentPage.children && currentPage.children.length > 0) {
                for (let i = 0; i < currentPage.children.length; i++) {
                    const child = currentPage.children[i];
                    const childResult = findPageRecursive(child, updatedParents);

                    if (childResult) {
                        return { ...childResult, index: i };
                    }
                }
            }

            return undefined;
        }

        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            const result = findPageRecursive(page);

            if (result) {
                return result;
            }
        }

        return undefined;
    }

    /**
     * Retrieves leasing data for a given car.
     *
     * This function fetches leasing data from the leasing service, processes it, and updates the component's leasing data.
     * It also determines whether the car is leasable based on the received data.
     *
     * @param {any} carData - The car data object.
     * @return {void}
     */
    getLeasingData(carData: ICarDetails) {
        // Leasing Data
        const umbracoLeasingData$ = this.leasingService.getLeasingGlobalData();
        const leasingValidIntervals$ = this.leasingService.getUsedCarLeasingValidIntervals(this.carIntervalsReqBody);
        const connectStream$ = combineLatest([umbracoLeasingData$, leasingValidIntervals$]);

        connectStream$.pipe(
            map(([umbracoLeasingData, leasingValidIntervals]) => {
                this.leasingDataTexts = umbracoLeasingData?.texts;

                if (!leasingValidIntervals || objectIsEmpty(leasingValidIntervals)) {
                    this.isLeasable = false;
                    this.cdr.markForCheck();
                    return;
                } else {
                    this.createConfiguratorRanges(leasingValidIntervals, carData);

                    if (this.leasingConfiguratorRangesData.leasingMonthsRange.length) {
                        this.createConfiguratorData({
                            downPayment: carData?.price?.data ? this.leasingService.getDefaultDownPayment(carData.price.data, leasingValidIntervals.minDownPaymentPct ?? 25) : undefined,
                            monthlyPayment: undefined,
                            monthlyTerms: this.leasingConfiguratorRangesData.leasingMonthsRange.find(item => item.isDefault === true),
                            residualValue: 0,
                            yearlyDistanceInKm: this.leasingService.getDefaultYearlyDistanceInKmRange([this.leasingConfiguratorRangesData.yearlyDistanceInKmRange.minValue, this.leasingConfiguratorRangesData.yearlyDistanceInKmRange.maxValue]),
                            totalPrice: carData.cashPrice?.data
                        });
                    } else {
                        console.warn('[BN: Invalid leasing data received]');
                        return;
                    }

                    this.getUsedCarPricePerMonth().pipe(take(1)).subscribe((data: ILeasingUsedCarPriceResponse) => {
                        this.leasingConfiguratorData.monthlyPayment = Number(data?.pricePerMonth);

                        const leasingConfigData = {
                            leasingData: this.leasingConfiguratorData,
                            leasingRanges: this.leasingConfiguratorRangesData
                        };

                        this.leasingService.broadcastUsedCarLeasingData(leasingConfigData);
                        this.isLeasable = true;
                        this.cdr.markForCheck();
                    });
                }
            }), take(1)).subscribe();
    }

    getUsedCarPricePerMonth(): Observable<ILeasingUsedCarPriceResponse> {
        return this.leasingService.getUsedCarLeasingPrice(this.carDetails, this.leasingConfiguratorData);
    }

    /**
     * Creates and populates the leasing configurator data object.
     *
     * @param {any} data - The input data object containing leasing configurator data.
     * @return {void}
     */
    createConfiguratorData(data: any) {
        if (!data) {
            return;
        }

        // Configurator data
        this.leasingConfiguratorData.monthlyPayment = Number(data.monthlyPayment);
        this.leasingConfiguratorData.downPayment = Number(data.downPayment);
        this.leasingConfiguratorData.monthlyTerms = Number(data.monthlyTerms.value);
        this.leasingConfiguratorData.yearlyDistanceInKm = Number(data.yearlyDistanceInKm);
        this.leasingConfiguratorData.residualValue = Number(data.residualValue);
        this.leasingConfiguratorData.totalPrice = Number(data.totalPrice);

        this.cdr.markForCheck();
    }

    /**
     * Creates and populates the leasing configurator ranges data object.
     *
     * @param {any} intervalsData - The input data object containing leasing intervals data.
     * @param {any} carData - The input data object containing car data.
     * @return {void}
     */
    createConfiguratorRanges(intervalsData: any, carData: any) {
        if (!intervalsData || !intervalsData.maxDurationMonths) {
            return;
        }

        // Create ranges data
        if (intervalsData.minDurationMonths && intervalsData.maxDurationMonths) {
            this.leasingConfiguratorRangesData.leasingMonthsRange = this.leasingService.createMonthsRange(intervalsData.minDurationMonths, intervalsData.maxDurationMonths);
        }

        if (intervalsData.minKm && intervalsData.maxKm) {
            this.leasingConfiguratorRangesData.yearlyDistanceInKmRange = {
                minValue: intervalsData.minKm,
                maxValue: intervalsData.maxKm
            };
        }

        if ((intervalsData.minDownPaymentPct || intervalsData.minDownPaymentPct === 0) && intervalsData.maxDownPaymentPct) {
            this.leasingConfiguratorRangesData.downPaymentRange = this.leasingService.createDownPaymentRange(intervalsData, carData.cashPrice?.data);

            // TODO: TEMPORARY data until BE/Loucon provides correct data
            this.leasingConfiguratorRangesData.residualValueRange = {
                minValue: 0,
                maxValue: 0
            };
        }
    }

    /**
     * Handles the position of the model header based on the current scroll position.
     *
     * @param {number} position - The current scroll position.
     * @return {void}
     */
    handleModelHeaderPosition(position: number) {
        if (!this.headerStickyScrollRef) {
            return;
        }

        this.stickyHeaderElementYPosition = this.headerStickyScrollRef.nativeElement.getBoundingClientRect().top + window.scrollY;

        if (this.componentLayout === LayoutView.SMALL) {
            if (window.scrollY < this.stickyHeaderElementYPosition) {
                this.stickyHeaderIsFixed = false;
                this.moveMenuDown = false;
                this.stickyHeaderElementHeight = 'auto';
            } else {
                this.moveMenuDown = position < this.prevScrollPosition;
            }
            if (window.scrollY >= this.stickyHeaderElementYPosition) {
                this.stickyHeaderIsFixed = true;
                this.stickyHeaderElementHeight = `${this.stickyHeaderElement.clientHeight}px`;
            }
        } else {
            if (window.scrollY <= 0) {
                this.stickyHeaderIsFixed = false;
                this.moveMenuDown = false;
                this.stickyHeaderElementHeight = 'auto';
            } else {
                this.moveMenuDown = position < this.prevScrollPosition;
            }
            if (window.scrollY >= this.stickyHeaderElementYPosition) {
                this.stickyHeaderIsFixed = true;
                this.stickyHeaderElementHeight = `${this.stickyHeaderElement.clientHeight}px`;
            }
        }

        this.prevScrollPosition = position;
        this.cdr.markForCheck();
    }

    /**
     * Updates the width of the scrolling div based on the offset width of its parent element.
     *
     * @return {void} This function does not return a value.
     */
    updateScrollingSidebarWidth() {
        this.scrollingSidebarRef.nativeElement.style.width = `${this.scrollingSidebarParentElement.offsetWidth}px`;
    }

    /**
     * Updates the position of the scrolling div based on the current scroll position.
     *
     * This function checks the scroll position and updates the position of the scrolling div accordingly.
     * It takes into account the parent element's position, the scroll direction, and the offset of the sticky header.
     *
     * @return {void}
     */
    updateScrollingSidebarPosition() {
        // If it is a small layout we do nothing
        if (this.componentLayout === LayoutView.SMALL) {
            return;
        }

        const parentRect = this.scrollingSidebarParentElement.getBoundingClientRect();
        const parentTop = parentRect.top + window.scrollY;
        const parentBottom = parentRect.bottom + window.scrollY;
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const headerExtraOffset = this.componentLayout === LayoutView.SMALL ? 0 : 116;
	    this.scrollingSidebarOffsetWithStickyHeader = this.componentLayout === LayoutView.SMALL ? 0 : 130;
        const transitionProps = 'top 300ms cubic-bezier(0.23, 0, 0.13, 1)';

        const sidebarHeight = this.scrollingSidebarElement.offsetHeight;
        const viewportHeight = window.innerHeight;

        if (this.staticSidebar) {
            if (this.moveMenuDown) {
                if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader >= parentTop && scrollTop + this.scrollingSidebarOffsetWithStickyHeader <= (parentBottom - this.scrollingSidebarElement.offsetHeight) - headerExtraOffset) {
                    // Make sure we do not apply transition when position changes between absolute and fixed, as it will make the sidebar fly in
                    if (this.scrollingSidebarElement.style.position === 'absolute') {
                        this.scrollingSidebarElement.style.transition = 'none';
                    } else {
                        this.scrollingSidebarElement.style.transition = transitionProps;
                    }
                    this.scrollingSidebarElement.style.position = 'fixed';
                    this.scrollingSidebarElement.style.left = `${parentRect.left}px`;
                    this.scrollingSidebarElement.style.top = `${this.scrollingSidebarOffsetWithStickyHeader + headerExtraOffset}px`;
                }
            } else if (scrollTop + viewportHeight >= parentTop + sidebarHeight + 30 && scrollTop + viewportHeight <= parentBottom) {
                if (this.scrollingSidebarElement.style.position === 'absolute') {
                    this.scrollingSidebarElement.style.transition = 'none';
                } else {
                    this.scrollingSidebarElement.style.transition = transitionProps;
                }
                this.scrollingSidebarElement.style.position = 'fixed';
                this.scrollingSidebarElement.style.left = `${parentRect.left}px`;
                this.scrollingSidebarElement.style.top = `${viewportHeight - sidebarHeight - 30}px`; // Align bottom of the sidebar with the bottom of the viewport
            } // When sidebar reaches TOP
            else if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader + headerExtraOffset <= parentTop) {
                this.scrollingSidebarElement.style.transition = 'none';
                this.scrollingSidebarElement.style.position = 'absolute';
                this.scrollingSidebarElement.style.top = '0px';
                this.scrollingSidebarElement.style.left = '0px';
            }
            // When sidebar reaches BOTTOM
            else if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader > (parentBottom - this.scrollingSidebarElement.offsetHeight)) {
                // Make sure we do not apply transition when position changes between absolute and fixed, as it will make the sidebar fly in
                this.scrollingSidebarElement.style.transition = 'none';
                this.scrollingSidebarElement.style.position = 'absolute';
                this.scrollingSidebarElement.style.top = `${parentBottom - parentTop - this.scrollingSidebarElement.offsetHeight}px`;
                this.scrollingSidebarElement.style.left = '0px';
            }
        } else {
            if (this.moveMenuDown) {
                // Scrolling UP
                if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader >= parentTop && scrollTop + this.scrollingSidebarOffsetWithStickyHeader <= (parentBottom - this.scrollingSidebarElement.offsetHeight) - headerExtraOffset) {
                    // Make sure we do not apply transition when position changes between absolute and fixed, as it will make the sidebar fly in
                    if (this.scrollingSidebarElement.style.position === 'absolute') {
                        this.scrollingSidebarElement.style.transition = 'none';
                    } else {
                        this.scrollingSidebarElement.style.transition = transitionProps;
                    }
                    this.scrollingSidebarElement.style.position = 'fixed';
                    this.scrollingSidebarElement.style.left = `${parentRect.left}px`;
                    this.scrollingSidebarElement.style.top = `${this.scrollingSidebarOffsetWithStickyHeader + headerExtraOffset}px`;
                }
                // When sidebar reaches TOP
                else if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader + headerExtraOffset <= parentTop) {
                    this.scrollingSidebarElement.style.transition = 'none';
                    this.scrollingSidebarElement.style.position = 'absolute';
                    this.scrollingSidebarElement.style.top = '0px';
                    this.scrollingSidebarElement.style.left = '0px';
                }
            } else {
                // Scrolling DOWN
                if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader >= parentTop && scrollTop + this.scrollingSidebarOffsetWithStickyHeader <= (parentBottom - this.scrollingSidebarElement.offsetHeight)) {
                    // Make sure transition is correct when scrolling down
                    if (this.scrollingSidebarElement.style.position === 'absolute') {
                        this.scrollingSidebarElement.style.transition = 'none';
                    } else {
                        this.scrollingSidebarElement.style.transition = transitionProps;
                    }
                    this.scrollingSidebarElement.style.position = 'fixed';
                    this.scrollingSidebarElement.style.left = `${parentRect.left}px`;
                    this.scrollingSidebarElement.style.top = `${this.scrollingSidebarOffsetWithStickyHeader}px`;
                }
                // When sidebar reaches BOTTOM
                else if (scrollTop + this.scrollingSidebarOffsetWithStickyHeader > (parentBottom - this.scrollingSidebarElement.offsetHeight)) {
                    // Make sure we do not apply transition when position changes between absolute and fixed, as it will make the sidebar fly in
                    this.scrollingSidebarElement.style.transition = 'none';
                    this.scrollingSidebarElement.style.position = 'absolute';
                    this.scrollingSidebarElement.style.top = `${parentBottom - parentTop - this.scrollingSidebarElement.offsetHeight}px`;
                    this.scrollingSidebarElement.style.left = '0px';
                }
            }
        }
    }

    /**
     * Scrolls to a specific section on the page.
     *
     * @param {UsedCarPageSections} anchor - The section to scroll to.
     * @return {void}
     */
    scrollToSection(anchor: UsedCarPageSections) {
        if (this.featureDetectionService.isBrowser()) {
            this.deactivateScrollTrigger = true;
            this.activeScrollMenuItem = anchor;

            if (anchor === UsedCarPageSections.OVERVIEW) {
                this.scroller.scrollToPosition([0, 0]);
            } else {
                const index = this.getActiveAnchorIndex(anchor);
                this.scrollToListItem(index);
                this.scroller.scrollToAnchor(anchor);
                this.cdr.markForCheck();
            }

            const scrollerTimeout = setTimeout(() => {
                this.deactivateScrollTrigger = false;
                clearTimeout(scrollerTimeout);
                this.cdr.markForCheck();
            }, 1000);
        }
    }

    /**
     * Scrolls the mobile navigation list horizontally to the specified item index.
     *
     * @param {number} index - The index of the item to scroll to.
     * @return {void}
     */
    scrollToListItem(index: number): void {
        if (!this.listElements && index !== -1) {
            return;
        }

        if (this.featureDetectionService.isBrowser()) {
            if (!this.listElementsArray[index]) {
                return;
            }

            let timeouter;
            clearTimeout(timeouter);

            const element: HTMLElement = this.listElementsArray[index].nativeElement;
            const parentElement = element.parentElement;

            if (parentElement) {
                let scrollDistance: number;

                if (index + 1 === this.listElementsArray.length) {
                    scrollDistance = parentElement.offsetWidth;
                } else {
                    scrollDistance = element.offsetLeft - parentElement.scrollLeft - (parentElement.offsetWidth / 2) + (element.offsetWidth / 2);
                }

                if (this.isIOS) {
                    timeouter = setTimeout(() => {
                        parentElement?.scrollBy({
                            top: 0,
                            left: scrollDistance,
                            behavior: 'auto', // IOS does not handle smooth scrolling well in this scenario
                        });
                    }, 1000);
                } else {
                    parentElement?.scrollBy({
                        top: 0,
                        left: scrollDistance,
                        behavior: 'smooth',
                    });
                }
            }
        }
    }

    getActiveAnchorIndex(anchor: UsedCarPageSections): number {
        switch (anchor) {
            case UsedCarPageSections.OVERVIEW:
                return 0;
            case UsedCarPageSections.EQUIPMENT:
                return 1;
            case UsedCarPageSections.SPECIFICATIONS:
                return 2;
            // case UsedCarPageSections.SERVICE: // TODO: Add services when needed
            //     return 3;
            case UsedCarPageSections.EXCHANGE_CALCULATION:
                return 3;
            case UsedCarPageSections.INSURANCE:
                return 4;
            default:
                return 0;
        }
    }

    /**
     * Calculates the positions and heights of various sections within the component.
     *
     * Iterates through the component's references (overview, equipment, specifications, service, exchange calculation, insurance, and last section)
     * and calculates their Y positions and heights using the elementYPosition and elementHeight methods.
     *
     * @return {void}
     */
    calculateSections() {
        if (this.overviewRef) {
            const overviewElement = this.overviewRef.nativeElement;
            this.overviewElementYPosition = this.elementYPosition(overviewElement);
            this.overviewElementHeight = this.elementHeight(overviewElement);
        }

        if (this.equipmentRef) {
            const equipmentsElement = this.equipmentRef.nativeElement;
            this.equipmentsElementYPosition = this.elementYPosition(equipmentsElement);
            this.equipmentsElementHeight = this.elementHeight(equipmentsElement);
        }

        if (this.specificationsRef) {
            const specificationsElement = this.specificationsRef.nativeElement;
            this.specificationsElementYPosition = this.elementYPosition(specificationsElement);
            this.specificationsElementHeight = this.elementHeight(specificationsElement);
        }

        if (this.serviceRef) {
            const serviceElement = this.serviceRef.nativeElement;
            this.serviceElementYPosition = this.elementYPosition(serviceElement);
            this.serviceElementHeight = this.elementHeight(serviceElement);
        }

        if (this.exchangeCalculationRef) {
            const exchangeCalculationElement = this.exchangeCalculationRef.nativeElement;
            this.exchangeCalculationElementYPosition = this.elementYPosition(exchangeCalculationElement);
            this.exchangeCalculationElementHeight = this.elementHeight(exchangeCalculationElement);
        }

        if (this.insuranceRef) {
            const insuranceElement = this.insuranceRef.nativeElement;
            this.insuranceElementYPosition = this.elementYPosition(insuranceElement);
            this.insuranceElementHeight = this.elementHeight(insuranceElement);
        }

        if (this.lastSectionRef) {
            const lastSectionElement = this.lastSectionRef.nativeElement;
            this.lastSectionElementYPosition = this.elementYPosition(lastSectionElement);
        }
    }

    elmentScrollTriggerPosition(scrollY: number, elementYPos: number, elementHeight: number): boolean {
        return scrollY + this.sectionsScrollOffset >= elementYPos && scrollY <= elementYPos + elementHeight;
    }

    elementYPosition(element: HTMLElement): number {
        return element.getBoundingClientRect().top + window.scrollY;
    }

    elementHeight(element: HTMLElement): number {
        return element.clientHeight > window.innerHeight / 2 ? element.clientHeight : window.innerHeight / 2;
    }

    /**
     * Selects / deselects an insurance company and updates the checkout service.
     *
     * @param {IExpandedInsuranceCompany} insurance - The insurance company to be selected.
     * @return {void}
     */
    selectInsurance(insuranceCompany: IExpandedInsuranceCompany) {
        // Not the same company as selectedInsuranceCompany
        if (this.selectedInsuranceCompany?.companyName !== insuranceCompany.companyName) {
            this.selectedInsuranceCompany = { ...insuranceCompany, isSelected: true };
            this.insuranceCompanies = this.insuranceCompanies.map((company: IExpandedInsuranceCompany) => ({
                ...company,
                isSelected: company.companyName === this.selectedInsuranceCompany?.companyName ? true : false
            }));
        } else {
            // Same company as selectedInsuranceCompany, toggle selected value
            this.selectedInsuranceCompany = null; // Force changes to flow into child components
            this.selectedInsuranceCompany = { ...insuranceCompany, isSelected: !insuranceCompany.isSelected };
            this.insuranceCompanies = this.insuranceCompanies.map((company: IExpandedInsuranceCompany) => ({
                ...company,
                isSelected: company.companyName === this.selectedInsuranceCompany?.companyName ? this.selectedInsuranceCompany.isSelected : false
            }));
        }

        if (this.selectedInsuranceCompany.isSelected) {
            if (this.selectedInsuranceCompany?.companyName) {
                this.checkoutService.setInsuranceOnPDP(this.selectedInsuranceCompany.companyName).subscribe({
                    error: (error) => {
                        console.error('Error setting insurance', error);
                    }
                });
            }
        } else {
            // Deselect insurance
            this.checkoutService.setInsuranceOnPDP('').subscribe({
                error: (error) => {
                    console.error('Error setting insurance', error);
                }
            });
        }

        this.cdr.markForCheck();
    };

    /**
     * Opens the exchange car side panel.
     *
     * @return {void}
     */
    showExhangeCarSidepanel() {
        this.sidepanelService.showExhangeCarSidePanel(true);
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.leasingService.broadcastUsedCarLeasingData({});
        // Disconnect the MutationObserver to avoid memory leaks
        if (this.mutationObserver) {
            this.mutationObserver.disconnect();
        }
    }
}
